<script setup lang="ts">
import RcSiteSelect, { type ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import { getBaselineResetDaysFlow, getBaselineSimulator } from '@/services/eos/baseline-reset-days-tracker.service'
import { SiteType } from '@/types/core/Site'
import { formatISODateToDateString, formatMegawatts, formatMWh } from '@/util/formatters'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Icon } from '@iconify/vue'
import { useQuery } from '@tanstack/vue-query'
import { DateTime } from 'luxon'
import { computed, ref } from 'vue'

const selectedSite = ref<ISiteIdentifier>()
const selectedSiteId = computed(() => selectedSite.value?.site_id)
const selectedDay = ref<string | null>(null)

const selectedDayISO = computed(() => {
  if (!selectedDay.value) return null
  // selectedDay.value is already in YYYY-MM-DD format when value-format is used
  return selectedDay.value
})

const isQueryEnabled = computed(() => !!selectedSiteId.value && !!selectedDayISO.value)

const baselineSimulatorQuery = useQuery({
  queryFn: () => getBaselineSimulator(selectedSiteId.value!, selectedDayISO.value!),
  queryKey: ['baseline-simulator', selectedSiteId, selectedDayISO],
  enabled: isQueryEnabled,
})

const shouldShowError = computed(() => {
  return (
    baselineSimulatorQuery.isError.value &&
    baselineSimulatorQuery.error.value &&
    !baselineSimulatorQuery.isRefetching.value
  )
})

// reset_days entries carry an ISO `date` (already in YYYY-MM-DD format) plus a `total_flow_mwh`
const resetDaysFormatted = computed(() => {
  if (!baselineSimulatorQuery.data.value?.reset_days) return []
  return baselineSimulatorQuery.data.value.reset_days.filter((resetDay) => Boolean(resetDay?.date))
})

const isResetDaysTableExpanded = ref(false)
const isFlowQueryEnabled = computed(() => isResetDaysTableExpanded.value && isQueryEnabled.value)

const resetDaysFlowQuery = useQuery({
  queryFn: () => getBaselineResetDaysFlow(selectedSiteId.value!, selectedDayISO.value!),
  queryKey: ['baseline-reset-days-flow', selectedSiteId, selectedDayISO],
  enabled: isFlowQueryEnabled,
})

const shouldShowFlowError = computed(() => {
  return resetDaysFlowQuery.isError.value && resetDaysFlowQuery.error.value && !resetDaysFlowQuery.isRefetching.value
})

const HOUR_ENDINGS = Array.from({ length: 24 }, (_, i) => i + 1)

// One column per reset day date; rows are hour-endings 1-24, since flow_mw_by_hour keys aren't
// guaranteed to be a contiguous 1-24 on DST-transition days
const resetDaysFlowRows = computed(() => {
  const flows = resetDaysFlowQuery.data.value?.reset_day_flows ?? []
  return HOUR_ENDINGS.map((hourEnding) => {
    const row: Record<string, number | null> = { hour_ending: hourEnding }
    for (const flow of flows) {
      row[flow.date] = flow.flow_mw_by_hour?.[String(hourEnding)] ?? null
    }
    return row
  })
})

const resetDaysFlowColumns = computed(
  () => resetDaysFlowQuery.data.value?.reset_day_flows?.map((flow) => flow.date).filter(Boolean) ?? []
)

// Column headers only: compact mm/dd, no leading zeroes, no year
const formatDateAsShortMonthDay = (isoDate: string): string => {
  const dt = DateTime.fromISO(isoDate)
  return dt.isValid ? dt.toFormat('M/d') : isoDate
}

// raw_baseline_by_hour is now an array of structured objects, already sorted
const rawBaselineByHourArray = computed(() => {
  const data = baselineSimulatorQuery.data.value
  if (!data?.raw_baseline_by_hour) return []

  const isoTimezone = data.iso_timezone

  return data.raw_baseline_by_hour.map((item) => {
    const dt = DateTime.fromISO(item.datetime)
    if (!dt.isValid) {
      return {
        ...item,
        datetime_formatted: item.datetime,
        hour_ending: null,
      }
    }

    const localizedDt = isoTimezone ? dt.setZone(isoTimezone) : dt

    return {
      ...item,
      datetime_formatted: localizedDt.toFormat('yyyy-MM-dd HH:mm:ss'),
      hour_ending: localizedDt.hour + 1,
    }
  })
})
</script>

<template>
  <ViewLayout>
    <el-card class="w-full max-w-[1280px] self-center">
      <template #header>
        <div class="flex flex-col md:flex-row gap-4 items-center">
          <RcSiteSelect v-model="selectedSite" class="w-64" :type="SiteType.DataCenter" clearable />
          <el-date-picker
            v-model="selectedDay"
            type="date"
            placeholder="Pick a day"
            class="w-48"
            value-format="YYYY-MM-DD"
          />
        </div>
      </template>

      <div v-if="!selectedSite || !selectedDay" class="h-128 flex items-center justify-center gap-2 text-text-tertiary">
        <Icon icon="mdi:alert-circle-outline" height="20" />
        <span>Please select a site and a day to view the baseline simulation.</span>
      </div>

      <template v-else-if="isQueryEnabled">
        <template v-if="shouldShowError">
          <rl-error-result :error="baselineSimulatorQuery.error.value">
            <el-button @click="() => baselineSimulatorQuery.refetch()">
              <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
            </el-button>
          </rl-error-result>
        </template>

        <div
          v-else-if="baselineSimulatorQuery.isPending && !baselineSimulatorQuery.data?.value"
          class="h-128 flex items-center justify-center gap-2 text-text-tertiary"
        >
          <Icon icon="eos-icons:three-dots-loading" height="20" />
          <span>Loading...</span>
        </div>

        <div
          v-else-if="baselineSimulatorQuery.isSuccess && !baselineSimulatorQuery.data?.value"
          class="h-128 flex items-center justify-center gap-2 text-text-tertiary"
        >
          <Icon icon="mdi:alert-circle-outline" height="20" />
          <span>No data available</span>
        </div>

        <div v-else-if="baselineSimulatorQuery.data?.value" class="flex flex-col gap-6">
          <!-- Lookup Period Dates -->
          <el-card>
            <template #header>
              <h3 class="text-lg font-semibold">Lookup Period</h3>
            </template>
            <div class="flex flex-col gap-4">
              <div class="flex flex-row gap-4">
                <div class="flex flex-col gap-1">
                  <span class="text-text-tertiary text-sm">Start Date</span>
                  <span class="text-base">
                    {{ formatISODateToDateString(baselineSimulatorQuery.data.value.lookup_period_start_date) }}
                  </span>
                </div>
                <div class="flex flex-col gap-1">
                  <span class="text-text-tertiary text-sm">End Date</span>
                  <span class="text-base">
                    {{ formatISODateToDateString(baselineSimulatorQuery.data?.value?.lookup_period_end_date) }}
                  </span>
                </div>
                <div class="flex flex-col gap-1">
                  <span class="text-text-tertiary text-sm">Site's ISO Timezone</span>
                  <span class="text-base">
                    {{ baselineSimulatorQuery.data?.value?.iso_timezone }}
                  </span>
                </div>
              </div>
            </div>
          </el-card>

          <!-- Reset Days -->
          <el-card>
            <template #header>
              <h3 class="text-lg font-semibold">Reset Days</h3>
            </template>
            <div v-if="resetDaysFormatted.length === 0" class="text-text-tertiary">No reset days found</div>

            <template v-else>
              <div v-if="!isResetDaysTableExpanded" class="flex flex-wrap gap-2">
                <el-tooltip
                  v-for="resetDay in resetDaysFormatted"
                  :key="resetDay.date"
                  :content="`Total flow: ${formatMWh(resetDay.total_flow_mwh)}`"
                  placement="top"
                >
                  <el-tag type="info">{{ formatISODateToDateString(resetDay.date) }}</el-tag>
                </el-tooltip>
              </div>

              <template v-else>
                <template v-if="shouldShowFlowError">
                  <rl-error-result :error="resetDaysFlowQuery.error.value">
                    <el-button @click="() => resetDaysFlowQuery.refetch()">
                      <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
                    </el-button>
                  </rl-error-result>
                </template>

                <div
                  v-else-if="resetDaysFlowQuery.isPending.value && !resetDaysFlowQuery.data?.value"
                  class="h-32 flex items-center justify-center gap-2 text-text-tertiary"
                >
                  <Icon icon="eos-icons:three-dots-loading" height="20" />
                  <span>Loading...</span>
                </div>

                <el-table v-else :data="resetDaysFlowRows" class="reset-days-flow-table">
                  <el-table-column prop="hour_ending" label="Hour Ending" width="120" fixed />
                  <el-table-column
                    v-for="date in resetDaysFlowColumns"
                    :key="date"
                    :prop="date"
                    :label="formatDateAsShortMonthDay(date)"
                  >
                    <template #default="{ row }">
                      {{ formatMegawatts(row[date]) }}
                    </template>
                  </el-table-column>
                </el-table>
                <span class="text-xs text-text-tertiary">Expressed in MW</span>
              </template>

              <div class="flex justify-center mt-4">
                <el-button link type="primary" @click="isResetDaysTableExpanded = !isResetDaysTableExpanded">
                  {{ isResetDaysTableExpanded ? 'Collapse' : 'Expand' }}
                </el-button>
              </div>
            </template>
          </el-card>

          <!-- Raw Baseline by Hour -->
          <el-card>
            <template #header>
              <h3 class="text-lg font-semibold">
                Raw Baseline by Hour ({{ baselineSimulatorQuery.data?.value?.iso_timezone }})
              </h3>
            </template>
            <el-table :data="rawBaselineByHourArray">
              <el-table-column prop="datetime_formatted" label="Date & Time">
                <template #default="{ row }">
                  {{ row.datetime_formatted }}
                </template>
              </el-table-column>
              <el-table-column prop="hour_ending" label="Hour Ending">
                <template #default="{ row }">
                  {{ row.hour_ending }}
                </template>
              </el-table-column>
              <el-table-column prop="baseline_mw" label="Baseline (MW)">
                <template #default="{ row }">
                  {{ formatMegawatts(row.baseline_mw) }}
                </template>
              </el-table-column>
            </el-table>
          </el-card>
        </div>
      </template>
    </el-card>
  </ViewLayout>
</template>

<style scoped lang="scss">
:deep() {
  .el-card {
    &__header {
      display: flex;
      align-items: center;
      flex-direction: row;
      gap: 12px;
    }
  }

  .reset-days-flow-table th .cell {
    white-space: nowrap;
  }
}
</style>
